import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  Zap,
  Clock,
  ArrowRight,
  PhoneCall,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";

export default function WhatCanTmsTherapyTreatPage() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/what-can-tms-therapy-treat.jpg";

  const faqs = [
    {
      q: "What conditions are FDA-cleared for TMS therapy?",
      a: "TMS therapy is FDA-cleared for Major Depressive Disorder (MDD), Treatment-Resistant Depression (TRD), Anxious Depression (MDD with comorbid anxiety), Obsessive-Compulsive Disorder (OCD), and Smoking Cessation.",
    },
    {
      q: "Can TMS therapy treat anxiety, PTSD, or bipolar depression off-label?",
      a: "Yes. Clinical research and off-label protocols demonstrate strong efficacy for generalized anxiety disorder (GAD), Post-Traumatic Stress Disorder (PTSD), bipolar depression, and chronic migraines under psychiatric supervision.",
    },
    {
      q: "Is TMS therapy non-invasive and drug-free?",
      a: "Yes. TMS uses targeted magnetic pulses to stimulate specific cortical brain regions. It requires no anesthesia, no oral medications, produces no systemic side effects, and allows patients to drive home immediately.",
    },
    {
      q: "Is TMS covered by health insurance?",
      a: "Yes. Major insurance providers (Medicare, Blue Cross Blue Shield, Aetna, Cigna, UnitedHealthcare, Humana) cover TMS for Major Depressive Disorder when criteria are met.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="What Can TMS Therapy Treat? Conditions & Applications | Neuro Wellness"
        description="Discover all FDA-cleared and off-label conditions treated by Transcranial Magnetic Stimulation (TMS), including depression, anxiety, OCD, PTSD, and bipolar depression in Coral Springs, FL."
        canonical="/what-can-tms-therapy-treat"
        keywords="what can tms treat, tms therapy conditions, tms for depression, tms for anxiety, tms for ocd, tms for ptsd, tms therapy Coral Springs FL"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/what-can-tms-therapy-treat#webpage",
                name: "What Can TMS Therapy Treat? Conditions & Clinical Applications",
                url: "https://neurowellnesstms.com/what-can-tms-therapy-treat",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Comprehensive guide detailing FDA-cleared and evidence-based clinical indications for Transcranial Magnetic Stimulation (TMS) therapy.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/what-can-tms-therapy-treat#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Zap className="h-4 w-4" />
                    TMS Clinical Applications
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  What Can{" "}
                  <span className="text-accent">TMS Therapy Treat?</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Transcranial Magnetic Stimulation (TMS) is an advanced,
                  non-invasive neuromodulation technology that gently stimulates
                  targeted neural circuits to treat depression, anxiety, OCD,
                  PTSD, and more.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Brain className="h-4 w-4 text-accent" />
                    <span>FDA-Cleared Indications</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      TMS Candidacy
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Check TMS Eligibility
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Find out if your condition qualifies for TMS therapy at
                      our Coral Springs center.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="what-can-tms-therapy-treat"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "What Can TMS Treat",
                href: "/what-can-tms-therapy-treat",
              },
            ]}
          />
        </div>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          {/* FDA Cleared */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              FDA-Cleared Indications
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" /> Major
                  Depressive Disorder (MDD)
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  For adults with clinical depression or treatment-resistant
                  depression (TRD) who have not achieved satisfactory
                  improvement from prior oral antidepressant medications.
                </p>
              </div>

              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" /> Anxious
                  Depression
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  FDA-cleared to treat depressive symptoms accompanied by
                  anxiety symptoms in adult patients with Major Depressive
                  Disorder.
                </p>
              </div>

              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />{" "}
                  Obsessive-Compulsive Disorder (OCD)
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Targeted dTMS protocols stimulate deeper brain structures
                  (anterior cingulate cortex) to disrupt repetitive obsessive
                  thoughts and compulsive behaviors.
                </p>
              </div>

              <div className="rounded-xl border p-5 bg-card space-y-2">
                <h3 className="font-bold text-lg text-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" /> Smoking
                  Cessation
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  FDA-cleared deep TMS protocols target addiction pathways in
                  the insula and prefrontal cortex to reduce nicotine cravings.
                </p>
              </div>
            </div>
          </section>

          {/* Off-label / Clinical Indications */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Off-Label & Clinical Research Applications
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Extensive psychiatric clinical studies support the efficacy of
              targeted TMS protocols for:
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 text-sm text-foreground">
              {[
                "Post-Traumatic Stress Disorder (PTSD)",
                "Generalized Anxiety Disorder (GAD)",
                "Bipolar Depressive Episodes",
                "Chronic Migraines & Neuropathic Pain",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 bg-muted/30 p-3 rounded-lg border"
                >
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Explore Related TMS Treatment Pages
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link
                to="/tms-depression"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors block space-y-1"
              >
                <h4 className="font-bold text-sm text-foreground">
                  TMS for Depression
                </h4>
                <p className="text-xs text-muted-foreground">
                  FDA-cleared care for TRD
                </p>
              </Link>
              <Link
                to="/tms-anxiety"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors block space-y-1"
              >
                <h4 className="font-bold text-sm text-foreground">
                  TMS for Anxiety
                </h4>
                <p className="text-xs text-muted-foreground">
                  Anxious depression & GAD
                </p>
              </Link>
              <Link
                to="/tms-ocd"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors block space-y-1"
              >
                <h4 className="font-bold text-sm text-foreground">
                  TMS for OCD
                </h4>
                <p className="text-xs text-muted-foreground">
                  Deep magnetic stimulation
                </p>
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              Schedule Your TMS Candidacy Consultation
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              At Neuro Wellness TMS Centers of America in Coral Springs, FL, our
              medical experts offer personalized TMS evaluation and insurance
              verification.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Check Eligibility
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
